import { didYouMeanLabel, spellerNoResults } from './SearchSpellCorrectedSuggestion.locstring.json';
import loc, { formatToArray } from 'owa-localize';
import { observer } from 'mobx-react';
import { Link } from 'office-ui-fabric-react/lib/components/Link';
import { logUsage } from 'owa-analytics';
import { getScenarioStore, onSearchTextChanged, SearchScenarioId, startSearch } from 'owa-search';
import { lazyLogEntityClicked, SubstrateSearchScenario } from 'owa-search-service';
import { searchNaturalLanguageFirstLine } from 'owa-locstrings/lib/strings/searchnaturallanguagefirstline.locstring.json';
import { searchNaturalLanguageOriginalQuery } from 'owa-locstrings/lib/strings/searchnaturallanguageoriginalquery.locstring.json';
import { searchNaturalLanguageSecondLine } from 'owa-locstrings/lib/strings/searchnaturallanguagesecondline.locstring.json';

import * as React from 'react';

const classNames = require('classnames');
const styles = require('./SearchSpellCorrectedSuggestion.scss');

export interface SearchSpellCorrectedSuggestionProps {
    className: string;
}

@observer
export default class SearchSpellCorrectedSuggestion extends React.Component<
    SearchSpellCorrectedSuggestionProps,
    {}
> {
    render() {
        const { queryAlterationType } = getScenarioStore(SearchScenarioId.Mail);

        if (!queryAlterationType) {
            return null;
        }

        // Log that spelling suggestion was shown to the user.
        logUsage('Search_SearchSpellerRendered', [SearchScenarioId.Mail]);

        return (
            <div
                style={{ display: 'block' }}
                className={classNames(styles.spellerLabel, this.props.className)}>
                {this.getDRL()}
            </div>
        );
    }

    /**
     * This function gets which version of the DRL (decisions representative
     * language) to display to the user.
     */
    private getDRL = () => {
        const { queryAlterationType, recourseQuery: originalQuery } = getScenarioStore(
            SearchScenarioId.Mail
        );

        switch (queryAlterationType) {
            case 'Suggestion':
                return formatToArray(
                    loc(didYouMeanLabel),
                    <Link key="suggestedSearchTerm" onClick={this.changeSearchTerm}>
                        {this.getAlteredQueryString()}
                    </Link>
                );
            case 'NoResultModification':
                return formatToArray(
                    loc(spellerNoResults),
                    <Link key="suggestedSearchTerm" onClick={this.changeSearchTerm}>
                        {this.getAlteredQueryString()}
                    </Link>,
                    <strong>{originalQuery}</strong>
                );
            case 'NoRequeryModification':
                logUsage('Search_NaturalLanguageRecourseLinkRendered', [SearchScenarioId.Mail]);
                return (
                    <>
                        <div>{loc(searchNaturalLanguageFirstLine)}</div>
                        {formatToArray(
                            loc(searchNaturalLanguageSecondLine),
                            <Link
                                key="naturalLanguageRecourseLink"
                                onClick={this.onNaturalLanguageRecourseLinkClicked}>
                                <strong>{loc(searchNaturalLanguageOriginalQuery)}</strong>
                            </Link>
                        )}
                    </>
                );
            default:
                return null;
        }
    };

    private changeSearchTerm() {
        const { latestTraceId, suggestedSearchTermReferenceId, alteredQuery } = getScenarioStore(
            SearchScenarioId.Mail
        );

        // Log that user clicked on spelling suggestion.
        logUsage('Search_SearchSpellerClicked', [SearchScenarioId.Mail]);

        // Log that user clicked on spelling suggestion (for QF).
        lazyLogEntityClicked.importAndExecute(
            latestTraceId,
            suggestedSearchTermReferenceId,
            new Date(),
            SubstrateSearchScenario.Mail
        );

        // Update store and kick off search with altered query.
        onSearchTextChanged(alteredQuery, SearchScenarioId.Mail);
        startSearch('QueryAlterationRecourseLink', SearchScenarioId.Mail);
    }

    private onNaturalLanguageRecourseLinkClicked() {
        const { recourseQuery: originalQuery } = getScenarioStore(SearchScenarioId.Mail);

        // Log that user clicked on NL recourse link.
        logUsage('Search_NaturalLanguageRecourseLinkClicked', [SearchScenarioId.Mail]);

        // Re-issue search query (without language modification).
        onSearchTextChanged(originalQuery, SearchScenarioId.Mail);
        startSearch('QueryAlterationRecourseLink', SearchScenarioId.Mail);
    }

    /**
     * Gets altered query string for UI (i.e. bolding corrected words in
     * query string).
     */
    private getAlteredQueryString() {
        const { recourseQuery, flaggedTokens } = getScenarioStore(SearchScenarioId.Mail);

        const pieces = [];
        let modifiedRecourseQuery = recourseQuery;

        // Grab first part of string (if it doesn't start with a misspelled word).
        if (flaggedTokens[0].Offset > 0) {
            pieces.push(recourseQuery.substring(0, flaggedTokens[0].Offset));
        }

        // Iterate over flagged tokens returned by the service.
        for (let i = 0; i < flaggedTokens.length; i++) {
            const currentFlaggedToken = flaggedTokens[i];
            const { Suggestion: suggestion, Offset: offset, Length: length } = currentFlaggedToken;

            // Bold the correctly spelled version of the flagged token.
            pieces.push(<strong key={i}>{suggestion}</strong>);

            // Get the misspelled word from original query so we can remove it.
            const misspelledWord = modifiedRecourseQuery.substr(offset, length);

            // Split the query string so we only replace in the bad part.
            const properlySpelledPart = modifiedRecourseQuery.substring(0, offset);
            const partWithMisspelledWord = modifiedRecourseQuery.substring(offset);

            /**
             * Replace first instance of misspelled word in questionable part of
             * query with suggestion, and then merge the parts back together.
             */
            const regExp = new RegExp(misspelledWord);
            modifiedRecourseQuery =
                properlySpelledPart + partWithMisspelledWord.replace(regExp, suggestion);

            /**
             * Check if current flagged token is the last one. If it's not the
             * last one, add the correctly spelled bit between the 2 flagged
             * tokens. If it is the last one, just add the rest of the string.
             */
            const nextIndex = offset + suggestion.length;
            if (i < flaggedTokens.length - 1) {
                const nextFlaggedToken = flaggedTokens[i + 1];
                pieces.push(modifiedRecourseQuery.substring(nextIndex, nextFlaggedToken.Offset));
            } else {
                pieces.push(modifiedRecourseQuery.substring(nextIndex));
            }
        }

        return pieces;
    }
}
